/**
 * FelixRail — Felix-first minimal navigation (2026-06-06).
 *
 * Replaces the legacy `Sidebar` on the briefing home + every surface that
 * falls through AppShell's non-shelf / non-suppress branch. ShelfSidebar
 * still owns Orchestrator-mode projects; this rail owns everything else.
 *
 * The mess this fixes (Bob, 2026-06-06): the legacy sidebar shipped 12+
 * sections shouting at once, and its collapsed icons "stacked on top of
 * each other". FelixRail's answer:
 *   - Felix is the protagonist: a large brand-dark hero tile at the very
 *     top opens the Felix panel (the platform's primary affordance).
 *   - PRIMARY nav is a ruthless 4 daily-drivers (Home, Projects,
 *     Communications, Documents). Everything else is demoted into per-section
 *     collapsible headers; flag-gated briefing pages drop to Felix + ⌘K only.
 *     (Marketplace moved into the Projects "New Project ▸ From a template"
 *     flow — Bob 2026-06-08 — since it's a project-creation entry point, not a
 *     daily destination.)
 *   - The collapsed-stacking bug is made STRUCTURALLY impossible: every
 *     collapsed row is a fixed 44×44 `grid place-items-center` cell with
 *     `shrink-0` and NO rendered label text node (see §collapse contract).
 *
 * Widths reuse `--sidebar-width` (304 expanded / 72 collapsed), driven by
 * AppShell's `useLayoutEffect`, so Topbar/Felix-panel/main offsets and the
 * collapse-race regression guard all keep working unchanged.
 *
 * Brand: the Felix hero is the single deep-space (#020617) + teal (#2dd4bf)
 * "WOW" anchor (brand-dark in both themes, like the home/login hero Bob
 * likes). Everything else is semantic-token only → equal light/dark.
 */

import { useState, useEffect } from "react";
import { NavLink, useLocation, useParams, Link } from "react-router-dom";
import {
  LayoutGroup,
  // Alias to `m`: the app wraps everything in <LazyMotion strict>, which
  // throws on plain `motion.*` components. Strict mode requires the lightweight
  // `m` component (matches the rest of the app's convention).
  m,
  AnimatePresence,
  MotionConfig,
} from "framer-motion";
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronDown,
  MoreHorizontal,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import FTLIcon from "@/components/FTLIcon";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/stores/sidebar";
import { useFelixPanelStore } from "@/stores/felixPanel";
import { useAuthStore } from "@/stores/auth";
import { useProjectStore } from "@/stores/project";
import { useMyActionItems } from "@/api/hooks";
import { useV3Flag, V3_FLAGS } from "@/lib/featureFlagsV3";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  staggerCards,
  staggerCardItem,
  statusPulse,
  hoverGlow,
  springIn,
  tapShrink,
  useMotionVariants,
  useMotionTransition,
} from "@/lib/animations";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import {
  primaryGlobalItems,
  overflowGlobalSections,
  getProjectSections,
  type NavItem,
  type NavSection,
} from "./navData";

const ADMIN_ROLES = ["admin", "org_admin", "program_director"];

/** Single source for the Action Items badge — the only count the rail shows.
 *  Replaces the legacy 5-query `useBadgeCounts` with one open-status query. */
function useActionItemsCount(): number {
  const { data } = useMyActionItems({ status: "OPEN", limit: "1" });
  return data?.total ?? 0;
}

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-teal)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]";

// ---------------------------------------------------------------------------
// Felix hero — the protagonist affordance.
// ---------------------------------------------------------------------------

function FelixHero({ collapsed }: { collapsed: boolean }) {
  const felixOpen = useFelixPanelStore((s) => s.open);
  const heroVariants = useMotionVariants(springIn);
  const pulseVariants = useMotionVariants(statusPulse);
  const hoverTransition = useMotionTransition({ duration: 0.24 });

  const open = () => useFelixPanelStore.getState().toggle();

  const iconDisc = (
    <m.span
      className={cn(
        "grid place-items-center rounded-xl bg-[var(--color-accent-teal)]/20 text-[var(--color-accent-teal-text)]",
        collapsed ? "h-9 w-9" : "h-9 w-9 shrink-0",
      )}
      variants={pulseVariants}
      initial="idle"
      animate={felixOpen ? "pulse" : "idle"}
    >
      <Sparkles className="h-5 w-5 shrink-0" />
    </m.span>
  );

  if (collapsed) {
    const btn = (
      <m.button
        type="button"
        onClick={open}
        variants={heroVariants}
        initial="hidden"
        animate="visible"
        whileHover={hoverGlow}
        whileTap={tapShrink}
        transition={hoverTransition}
        aria-label="Ask Felix"
        aria-pressed={felixOpen}
        className={cn(
          // Deep-space brand hero — MUST stay opaque-dark in BOTH themes
          // (it carries white text). `glass-on-dark` was removed because its
          // translucent-white background-color is declared last in index.css
          // and overrode aurora-hero's opaque #020617, exposing the light
          // sidebar in light mode → white text on white. aurora-hero keeps the
          // opaque dark canvas + gradient; an explicit border restores the edge.
          "canvas-deep aurora-hero grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/10",
          felixOpen && "ring-2 ring-[var(--color-accent-teal)]",
          FOCUS_RING,
        )}
      >
        {iconDisc}
      </m.button>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          Ask Felix
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <m.button
      type="button"
      onClick={open}
      variants={heroVariants}
      initial="hidden"
      animate="visible"
      whileHover={hoverGlow}
      whileTap={tapShrink}
      transition={hoverTransition}
      aria-label="Ask Felix"
      aria-pressed={felixOpen}
      className={cn(
        // Deep-space brand hero — opaque-dark in BOTH themes (white text).
        // See collapsed variant above: dropped `glass-on-dark` (its
        // translucent bg overrode the dark canvas → white-on-white in light
        // mode); aurora-hero keeps the opaque dark bg + gradient.
        "canvas-deep aurora-hero flex min-h-[88px] w-full items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 text-left",
        felixOpen && "ring-2 ring-[var(--color-accent-teal)]",
        FOCUS_RING,
      )}
    >
      {iconDisc}
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-bold text-white">Ask Felix</span>
        <span className="truncate text-[11px] text-white/70">
          Your AI program manager
        </span>
      </span>
    </m.button>
  );
}

// ---------------------------------------------------------------------------
// Nav row — primary destinations + project / overflow links.
// ---------------------------------------------------------------------------

function RailRow({
  item,
  badge,
  collapsed,
}: {
  item: NavItem;
  badge?: number;
  collapsed: boolean;
}) {
  const Icon = item.icon as LucideIcon;
  const hoverTransition = useMotionTransition({ type: "spring", stiffness: 500, damping: 35 });

  const link = (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      // Collapse contract: a fixed 44×44 grid cell, shrink-0, NO gap and NO
      // label text node when collapsed — stacking is structurally impossible.
      className={({ isActive }) =>
        cn(
          "group relative flex items-center rounded-xl text-[13px] transition-colors duration-150",
          collapsed
            ? "h-11 w-11 shrink-0 place-items-center grid"
            : "w-full gap-3 px-3 py-2.5",
          isActive
            ? "font-semibold text-[var(--color-accent-teal-text)] bg-[var(--color-surface-secondary)]"
            : "font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)]",
          FOCUS_RING,
        )
      }
      aria-label={collapsed ? item.label : undefined}
    >
      {({ isActive }) => (
        <>
          {/* Shared layout accent bar — slides between active rows. */}
          {isActive && (
            <m.span
              layoutId="rail-active"
              className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--color-accent-teal)]"
            />
          )}
          <m.span
            className="grid place-items-center"
            whileHover={collapsed ? undefined : { x: 2 }}
            whileTap={tapShrink}
            transition={hoverTransition}
          >
            <Icon
              className={cn(
                "h-5 w-5 shrink-0 transition-colors",
                isActive
                  ? "text-[var(--color-accent-teal-text)]"
                  : "text-[var(--color-text-tertiary)] group-hover:text-[var(--color-text-secondary)]",
              )}
            />
          </m.span>
          {!collapsed && (
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          )}
          {badge != null && badge > 0 && (
            <span
              className={cn(
                collapsed
                  ? "absolute -top-0.5 -right-0.5 inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[var(--color-accent-teal)] px-1 text-[9px] font-semibold leading-none tabular-nums text-[var(--color-canvas-deep)]"
                  : "ml-auto inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-teal)] px-1.5 text-[10px] font-semibold leading-none tabular-nums text-[var(--color-canvas-deep)]",
              )}
            >
              {badge > 999 ? "999+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Overflow — per-section collapsible headers when expanded; "More" popover
// when collapsed.
//
// Bob 2026-06-08: "make the section headers able to be expanded and collapsed
// as opposed to dumping all of it in a 'More' tab." So in the expanded rail
// every demoted section (Workspace / Admin / Platform / project sections) is
// its own independently-collapsible header — the structure is visible and the
// user opens only what they want. State persists per section in localStorage
// so a chosen layout survives navigation. The 72px collapsed icon-rail keeps
// the single "More" popover (no room for headers there).
// ---------------------------------------------------------------------------

/** A single demoted destination — shared by the collapsible sections and the
 *  collapsed-rail "More" popover. */
function SubNavLink({ item }: { item: NavItem }) {
  const Icon = item.icon as LucideIcon;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        cn(
          "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] transition-colors",
          isActive
            ? "font-semibold text-[var(--color-accent-teal-text)] bg-[var(--color-surface-secondary)]"
            : "font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)]",
          FOCUS_RING,
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn(
              "h-4 w-4 shrink-0",
              isActive
                ? "text-[var(--color-accent-teal-text)]"
                : "text-[var(--color-text-tertiary)]",
            )}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

/** Read the persisted open/closed state for a rail section (jsdom-safe). */
function readSectionOpen(title: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(`ftl.rail.section.${title}`);
    if (v != null) return v === "1";
  } catch {
    /* localStorage unavailable — use the fallback */
  }
  return fallback;
}

/** Expanded-rail section: a clickable header that expands/collapses its items
 *  independently of the other sections, persisting its state. */
function CollapsibleNavSection({
  section,
  defaultOpen = false,
}: {
  section: NavSection;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(() => readSectionOpen(section.title, defaultOpen));

  useEffect(() => {
    try {
      localStorage.setItem(`ftl.rail.section.${section.title}`, open ? "1" : "0");
    } catch {
      /* persistence is best-effort */
    }
  }, [open, section.title]);

  const panelId = `rail-section-${section.title.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          "group flex w-full items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)]",
          FOCUS_RING,
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">{section.title}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <m.div
            id={panelId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <ul className="mb-1 mt-0.5 space-y-0.5 pl-2">
              {section.items.map((item) => (
                <li key={item.path}>
                  <SubNavLink item={item} />
                </li>
              ))}
            </ul>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Collapsed-rail popover group — section title + its items (icon rail only). */
function OverflowGroup({ section }: { section: NavSection }) {
  return (
    <div className="px-1 py-1">
      <h3 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-tertiary)]">
        {section.title}
      </h3>
      <ul className="space-y-0.5">
        {section.items.map((item) => (
          <li key={item.path}>
            <SubNavLink item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Overflow({
  sections,
  collapsed,
}: {
  sections: NavSection[];
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (sections.length === 0) return null;

  // Expanded rail: each demoted section is its own collapsible header, so the
  // structure is visible and the user opens only what they want (no "More"
  // catch-all). Bob 2026-06-08.
  if (!collapsed) {
    return (
      <div className="flex flex-col gap-0.5">
        {sections.map((section) => (
          <CollapsibleNavSection key={section.title} section={section} />
        ))}
      </div>
    );
  }

  // Collapsed 72px icon-rail: there's no room for headers, so keep a single
  // "More" trigger that opens a right-side popover listing every section.
  return (
    <div className="relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="rail-overflow"
            aria-label="More"
            className={cn(
              "group relative grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[13px] font-medium text-[var(--color-text-secondary)] transition-colors duration-150 hover:bg-[var(--color-surface-secondary)]",
              FOCUS_RING,
            )}
          >
            <MoreHorizontal className="h-5 w-5 shrink-0 text-[var(--color-text-tertiary)] group-hover:text-[var(--color-text-secondary)]" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          More
        </TooltipContent>
      </Tooltip>
      <AnimatePresence initial={false}>
        {open && (
          <m.div
            id="rail-overflow"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-[60px] top-0 z-50 w-60 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-xl"
          >
            {sections.map((section) => (
              <OverflowGroup key={section.title} section={section} />
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FelixRail
// ---------------------------------------------------------------------------

export function FelixRail() {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const projectName = useProjectStore((s) => s.selectedProjectName);

  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);

  // Escape closes the mobile rail drawer (modal on small screens). No
  // focus-trap: the aside doubles as the persistent desktop rail.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen, setMobileOpen]);
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);

  const actionItemsCount = useActionItemsCount();

  const userRoles = (user?.roles ?? []).map((r) => r.toLowerCase());
  const isAdmin =
    userRoles.includes("org_admin") ||
    ADMIN_ROLES.some((r) => userRoles.includes(r));
  const isPlatformAdmin = user?.is_platform_admin === true;

  const isInProject = pathname.startsWith("/projects/") && !!projectId;

  // Template-layer vocabulary SKIN for the folded project-workspace group
  // (deduped fetch). Non-manifest projects keep verbatim labels via fallback.
  const { label, hiddenSurfaces } = useTemplateLabels(isInProject ? projectId : null);

  // Phase 7 (UX v2) v2Flag gating — the NavItem.v2Flag contract documented in
  // navData.ts, previously implemented only by the never-mounted NavPanel, so
  // v2 flags retired (PR-3: hardwired ON + rows purged) — every nav item that
  // used to be v2Flag-gated now renders unconditionally.
  const isV2FlagOn = (_key: string | undefined): boolean => true;

  // ITERATOR IS ONLY OFFERED WHERE IT OPENS. navData adds the Iterator entry
  // unconditionally, but pages/Iterator.tsx refuses with a "not enabled" panel
  // unless ftl.ux.v3.iterator_mvp is on for the org, and the flags endpoint
  // returns false for an org with no row at all. This rail is one of the two
  // mounted doors to that page (CommandPalette is the other); the never-mounted
  // Sidebar.tsx the audit row names is tree-shaken out of the bundle entirely,
  // so gating there would have changed nothing.
  //
  // Fails OPEN while the flag request is in flight: withholding a nav item on a
  // loading state makes the menu flicker, and the page itself still refuses.
  const iteratorFlag = useV3Flag(V3_FLAGS.ITERATOR_MVP);
  const iteratorHidden = !iteratorFlag.isLoading && !iteratorFlag.enabled;

  const dropGatedItems = (sections: NavSection[]): NavSection[] =>
    sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            isV2FlagOn(item.v2Flag) &&
            !(iteratorHidden && item.path?.endsWith("/iterator")),
        ),
      }))
      .filter((section) => section.items.length > 0);

  // Overflow visibility — same admin/platform gating as the legacy sidebar,
  // plus item-level v2Flag gating (fail-closed) on every overflow row.
  const visibleOverflow = dropGatedItems(
    overflowGlobalSections.filter((section) => {
      if (section.platformAdminOnly) return isPlatformAdmin;
      if (section.adminOnly) return isAdmin;
      return true;
    }),
  );

  // Inside a non-Orchestrator project, fold the project's PLAN/DESIGN/…
  // sections into a single "Project workspace" overflow group so deep
  // project pages stay reachable without re-exploding the rail. v2Flag-gated
  // BRIEFINGS rows are filtered here too — with every row gated off, the
  // whole BRIEFINGS section disappears (dropGatedItems drops empty sections).
  const projectOverflow: NavSection[] = isInProject
    ? dropGatedItems(getProjectSections(projectId, label, hiddenSurfaces))
    : [];

  const overflowSections = [...projectOverflow, ...visibleOverflow];

  const containerVariants = useMotionVariants(staggerCards);
  const itemVariants = useMotionVariants(staggerCardItem);

  function badgeFor(item: NavItem): number | undefined {
    if (item.badgeKey === "actionItems") return actionItemsCount;
    return undefined;
  }

  return (
    <MotionConfig reducedMotion="user">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-[var(--color-canvas-deep)]/40 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <TooltipProvider delayDuration={150}>
      <aside
        aria-label="Sidebar"
        className={cn(
          "fixed left-0 top-0 z-40 flex h-screen w-[var(--sidebar-width)] flex-col overflow-hidden",
          "border-r border-[var(--color-border)] bg-[var(--color-surface)]",
          "transition-transform duration-300 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Brand header — same height contract as the topbar so borders meet. */}
        <div
          className={cn(
            "flex h-[var(--topbar-height)] min-w-0 shrink-0 items-center border-b border-[var(--color-border)]",
            collapsed ? "justify-center px-0" : "justify-between px-5",
          )}
        >
          <Link
            to="/"
            className={cn(
              "flex min-w-0 items-center overflow-hidden",
              collapsed ? "justify-center" : "gap-2.5",
            )}
            aria-label="FTL home"
          >
            <FTLIcon size={40} interactive={false} />
            {!collapsed && (
              <span className="truncate text-lg font-bold tracking-tight text-[var(--color-text-primary)]">
                FTL
              </span>
            )}
          </Link>
          {!collapsed && (
            <button
              onClick={() => setMobileOpen(false)}
              className={cn(
                "rounded-lg p-1.5 text-[var(--color-text-tertiary)] hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)] lg:hidden",
                FOCUS_RING,
              )}
              aria-label="Close sidebar"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Felix hero — the protagonist. */}
        <div className={cn("shrink-0", collapsed ? "flex justify-center px-0 pt-4" : "px-3 pt-4")}>
          <FelixHero collapsed={collapsed} />
        </div>

        {/* Project context card (non-Orchestrator project, expanded only). */}
        {isInProject && !collapsed && (
          <div className="shrink-0 px-3 pt-4">
            <Link
              to="/projects"
              className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-text-tertiary)] transition-colors hover:text-[var(--color-text-secondary)]"
            >
              ← All projects
            </Link>
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)] p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent-teal-text)]">
                Project
              </div>
              <div className="mt-0.5 truncate text-sm font-semibold text-[var(--color-text-primary)]">
                {projectName || "Loading…"}
              </div>
            </div>
          </div>
        )}

        {/* Nav body. Collapsed: items-center + zero horizontal padding so the
            72px rail is never eaten; each row is a fixed grid cell. */}
        <LayoutGroup id="felix-rail">
            <m.nav
              aria-label="Primary navigation"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className={cn(
                "flex-1 min-w-0 overflow-y-auto overflow-x-hidden py-4",
                collapsed
                  ? "flex flex-col items-center gap-2 px-0"
                  : "flex flex-col gap-1 px-3",
              )}
            >
              {primaryGlobalItems.map((item) => (
                <m.div
                  key={item.path}
                  variants={itemVariants}
                  className={collapsed ? "shrink-0" : "min-w-0"}
                >
                  <RailRow item={item} badge={badgeFor(item)} collapsed={collapsed} />
                </m.div>
              ))}

              <m.div
                variants={itemVariants}
                className={collapsed ? "shrink-0" : "min-w-0"}
              >
                <Overflow sections={overflowSections} collapsed={collapsed} />
              </m.div>
            </m.nav>
          </LayoutGroup>

        {/* Footer — collapse toggle + ⌘K hint (verbatim from legacy). */}
        <div
          className={cn(
            "shrink-0 border-t border-[var(--color-border)]",
            collapsed ? "px-2 py-3" : "px-4 py-3",
          )}
        >
          <button
            onClick={toggleCollapsed}
            className={cn(
              "hidden w-full items-center rounded-lg text-[12px] font-medium transition-colors lg:flex",
              "text-[var(--color-text-tertiary)] hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)]",
              collapsed ? "h-9 justify-center" : "h-9 gap-2 px-3",
              FOCUS_RING,
            )}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronsLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
          {!collapsed && (
            <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--color-text-tertiary)]">
              <span>FTL</span>
              <kbd className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-tertiary)]">
                ⌘K
              </kbd>
            </div>
          )}
        </div>
      </aside>
      </TooltipProvider>
    </MotionConfig>
  );
}
